import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';

import { Box } from '@mui/material';

import { InternalAffairsApi } from '@/api/api.internal-affairs';
import { ConfirmationDialog, DataGrid } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { ICFS, IIACase, IKase, ITask } from '@/models';
import RelatedSearch from '@/pages/Kases/KaseOverview/Components/RelatedRecords/RelatedSearch';
import { RelatedRecordsTableColumns } from '@/pages/Tools/InternalAffairs/data';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

interface InternalAffairsRelatedRecordsContext {
  caseData: IIACase | null;
  setCaseData: React.Dispatch<React.SetStateAction<IIACase | null>>;
  showLoader?: () => void;
  hideLoader?: () => void;
  prefetchedSearchData?: ((ICFS | IKase | ITask) & {
    recordType: 'cfs' | 'kase' | 'task';
  })[];
}

export const InternalAffairsRelatedRecords = () => {
  const {
    caseData,
    setCaseData,
    showLoader,
    hideLoader,
    prefetchedSearchData,
  } = useOutletContext<InternalAffairsRelatedRecordsContext>();

  const { updateToast } = useToastStore();
  const { user } = useUserStore();

  // Check if we're in view mode from URL
  const isViewMode = window.location.pathname.includes('/view/');

  const [relatedItems, setRelatedItems] = useState<Array<IKase | ICFS>>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState<IKase | ICFS | null>(
    null,
  );

  // Initialize related items from case data and trigger refresh
  useEffect(() => {
    if (caseData) {
      const items: Array<IKase | ICFS> = [
        ...((caseData.relatedCfs as ICFS[]) || []),
        ...((caseData.relatedCase as IKase[]) || []),
      ];
      setRelatedItems(items);
      // Trigger refresh when data loads
      setRefreshKey((prev) => prev + 1);
    }
  }, [caseData]);

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `relatedRecordsIA-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        // In view mode, filter out the actions column
        if (isViewMode) {
          return RelatedRecordsTableColumns.filter(
            (col) => col.field !== 'actions',
          );
        }
        return RelatedRecordsTableColumns;
      }

      const merged = mergeColumnSettings(
        RelatedRecordsTableColumns,
        savedSettings,
      );
      // In view mode, filter out the actions column
      if (isViewMode) {
        return merged.filter((col) => col.field !== 'actions');
      }
      return merged;
    },
  );

  const handleUnlinkClick = useCallback((record: IKase | ICFS) => {
    setSelectedRecord(record);
    setShowConfirm(true);
  }, []);

  const columns = useDataGridColumns({
    type: 'relatedRecordsIA',
    columnSettings,
    actionMethods: isViewMode ? undefined : { onUnlink: handleUnlinkClick },
  });

  // Transform related items to rows with proper type indicator
  const rows = useMemo(() => {
    return relatedItems.map((item) => {
      const isKase = Boolean((item as IKase).address);
      return {
        ...item,
        id: item._id,
        _isKase: isKase,
      };
    });
  }, [relatedItems]);

  const unlink = async () => {
    const relatedItem = selectedRecord;
    if (!caseData?._id || !relatedItem?._id) return;

    try {
      showLoader?.();

      await InternalAffairsApi.removeRelatedRecords(caseData._id, [
        relatedItem._id,
      ]);

      // Update local state
      setRelatedItems((prev) =>
        prev.filter((item) => item._id !== relatedItem._id),
      );

      // Update case data to reflect the change
      if (setCaseData && caseData) {
        setCaseData({
          ...caseData,
          relatedCfs: (caseData.relatedCfs || []).filter(
            (cfs) => (cfs as ICFS)._id !== relatedItem._id,
          ),
          relatedCase: (caseData.relatedCase || []).filter(
            (kase) => (kase as IKase)._id !== relatedItem._id,
          ),
        });
      }

      updateToast({
        type: ConfirmTypes.SUCCESS,
        open: true,
        message: 'Record successfully unlinked',
      });
      setRefreshKey((prev) => prev + 1);
    } catch (err: any) {
      updateToast({
        open: true,
        message: err?.message || 'Failed to unlink record',
      });
    } finally {
      hideLoader?.();
      setSelectedRecord(null);
    }
  };

  const handleLinking = async (id: string, type: 'kase' | 'cfs' | 'task') => {
    if (!caseData?._id) return;

    // Only handle CFS and Kase (no tasks for IA)
    if (type === 'task') return;

    try {
      showLoader?.();

      const recordType = type === 'cfs' ? 'CFS' : 'KASE';
      await InternalAffairsApi.addRelatedRecords(caseData._id, [
        { recordType, recordId: id },
      ]);

      // Refetch case data to get properly populated fields (e.g. agency name)
      const response = await InternalAffairsApi.getOne(caseData._id);
      setCaseData(response.data);

      updateToast({
        type: ConfirmTypes.SUCCESS,
        open: true,
        message: 'Related record linked successfully',
      });
    } catch (err: any) {
      updateToast({
        open: true,
        message: err?.message || 'Failed to link record',
      });
    } finally {
      hideLoader?.();
    }
  };

  if (!caseData?._id) {
    return (
      <div className="pt-6 text-center text-gray-500">
        Please save the case first before adding related records.
      </div>
    );
  }

  return (
    <>
      <div className="pt-3">
        <DataGrid
          className="w-full"
          key={refreshKey}
          name="relatedRecordsIA"
          autoPageSize={false}
          clientMode={true}
          clientRows={rows}
          columns={columns}
          columnSettings={columnSettings}
          sx={{
            width: 'calc(100% - 20px)',
            minHeight: 300,
            '& .actions-cell': {
              backgroundColor: 'transparent !important',
            },
            '& .actions-cell:hover': {
              backgroundColor: 'transparent !important',
            },
          }}
          disableMultipleColumnsSorting={true}
          setColumnSettings={setColumnSettings}
          getRowId={(row) => row?._id || row?.id || ''}
          hideQuickFilter
          cta={
            !isViewMode ? (
              <Box sx={{ width: '500px' }}>
                <RelatedSearch
                  linkedMastertCFS={relatedItems as any}
                  makeLink={handleLinking}
                  forceEnable={true}
                  initialData={prefetchedSearchData}
                />
              </Box>
            ) : undefined
          }
        />
      </div>

      <ConfirmationDialog
        title="Unlink?"
        description="Are you sure you want to unlink this record?"
        confirmText="Yes"
        cancelText="No"
        open={showConfirm}
        onCancel={() => {
          setShowConfirm(false);
          setSelectedRecord(null);
        }}
        onConfirm={() => {
          unlink();
          setShowConfirm(false);
        }}
      />
    </>
  );
};
